import React, { useState, useEffect } from "react";

import {
  Mail,
  Search,
  Calendar,
  Loader,
} from "lucide-react";
import "../communication/SentEmailTable.css";

export default function AdminDraftEmail({ onEditDraft }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [loadingEmailId, setLoadingEmailId] = useState(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const apiAddress = process.env.REACT_APP_SECRET;

  useEffect(() => {
    fetchEmails();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const fetchEmails = async () => {
    try {
      setLoading(true);

      const res = await fetch(
        `${apiAddress}/api/SubSection/GetAdminDraftEmail?CreationId=admin&status=3`
      );
      const data = await res.json();
      setEmails(data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePreviewClick = async (emailId) => {
    try {
      setLoadingEmailId(emailId);

      const res = await fetch(
        `${apiAddress}/api/SubSection/GetEmailById/${emailId}`
      );
      const data = await res.json();
      onEditDraft?.(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingEmailId(null);
    }
  };

  // 🔍 Filter
  const filteredEmails = emails.filter(
    (e) =>
      e.sub?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.body?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 📄 Pagination
  const totalPages = Math.ceil(filteredEmails.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedEmails = filteredEmails.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  if (loading) {
    return (
      <div className="loader-screen">
        <Loader className="spinner" size={48} />
        <p>Loading emails...</p>
      </div>
    );
  }

  return (
    <div className="email-page">
      {/* Header */}
      <div className="page-header">
        <div className="title">
          <Mail size={32} />
          <div>
            <h1>Draft Emails</h1>
            <p>All emails drafted by Admin</p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="search-container">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by subject or content..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button className="refresh-btn" onClick={fetchEmails}>
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="table-card">
        <table>
          <thead>
            <tr>
              <th>Subject</th>
              <th>Preview</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {filteredEmails.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty">
                  No emails found
                </td>
              </tr>
            ) : (
              paginatedEmails.map((email) => (
                <tr key={email.id}>
                  <td className="subject">
                    {email.sub || "No Subject"}
                  </td>

                  <td
                    className="preview clickable"
                    onClick={() => handlePreviewClick(email.id)}
                  >
                    {loadingEmailId === email.id ? (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                        <Loader className="spinner" size={14} />
                        Opening editor...
                      </span>
                    ) : (
                      <>
                        {email.body
                          ?.replace(/<[^>]*>/g, "")
                          .slice(0, 90)}
                        ...
                      </>
                    )}
                  </td>

                  <td className="date">
                    <Calendar size={14} />
                    {new Date(email.creationDate).toLocaleDateString()}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
            >
              Prev
            </button>

            {[...Array(totalPages)].map((_, i) => (
              <button
                key={i}
                className={currentPage === i + 1 ? "active" : ""}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}

            <button
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
